// Achievement API Client
// Handles all achievement-related API calls

import config from '../config/environment';

export interface AchievementCondition {
  type: string;
  target?: number;
  gameType?: string;
  timeRange?: string;
  minMinutes?: number;
  maxMinutes?: number;
}

export interface Achievement {
  _id: string;
  id: string;
  name: string;
  description: string;
  category: 'gameplay' | 'competitive' | 'social' | 'progression' | 'special';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  icon: string;
  points: number;
  condition: AchievementCondition;
  createdAt: string;
  updatedAt: string;
}

export interface UserAchievement {
  achievement: Achievement;
  progress: number;
  target: number;
  completed: boolean;
  unlockedAt?: string;
  progressPercentage: number;
}

export interface RecentUnlock {
  achievement: Achievement;
  unlockedAt: string;
}

export interface AchievementStats {
  total: number;
  completed: number;
  inProgress: number;
  totalPoints: number;
  completionRate: number;
  totalAvailable: number;
}

class AchievementsApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.api.baseUrl;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  }

  /**
   * Get all achievements
   */
  async getAllAchievements(): Promise<Achievement[]> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/api/achievements`);

    if (!response.ok) {
      throw new Error('Failed to fetch achievements');
    }

    const data = await response.json();
    return data.data.achievements;
  }

  /**
   * Get achievements by category
   */
  async getAchievementsByCategory(category: string): Promise<Achievement[]> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/api/achievements/category/${category}`);

    if (!response.ok) {
      throw new Error('Failed to fetch achievements by category');
    }

    const data = await response.json();
    return data.data.achievements;
  }

  /**
   * Get achievements by tier
   */
  async getAchievementsByTier(tier: string): Promise<Achievement[]> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/api/achievements/tier/${tier}`);

    if (!response.ok) {
      throw new Error('Failed to fetch achievements by tier');
    }

    const data = await response.json();
    return data.data.achievements;
  }

  /**
   * Get user's achievements with progress
   */
  async getUserAchievements(userId: string): Promise<UserAchievement[]> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/api/achievements/user/${userId}`);

    if (!response.ok) {
      throw new Error('Failed to fetch user achievements');
    }

    const data = await response.json();
    return data.data.achievements;
  }

  /**
   * Get recently unlocked achievements
   */
  async getRecentUnlocks(userId: string, limit: number = 5): Promise<RecentUnlock[]> {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/api/achievements/user/${userId}/recent?limit=${limit}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch recent unlocks');
    }

    const data = await response.json();
    return data.data.recentUnlocks;
  }

  /**
   * Get user achievement statistics
   */
  async getUserStats(userId: string): Promise<AchievementStats> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/api/achievements/user/${userId}/stats`);

    if (!response.ok) {
      throw new Error('Failed to fetch user achievement stats');
    }

    const data = await response.json();
    return data.data.stats;
  }

  /**
   * Initialize achievements (admin only)
   */
  async initializeAchievements(): Promise<void> {
    const response = await this.fetchWithAuth(`${this.baseUrl}/api/achievements/initialize`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Failed to initialize achievements');
    }
  }

  /**
   * Manually unlock achievement (admin only)
   */
  async unlockAchievement(userId: string, achievementId: string): Promise<void> {
    const response = await this.fetchWithAuth(
      `${this.baseUrl}/api/achievements/unlock/${userId}/${achievementId}`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to unlock achievement');
    }
  }
}

export const achievementsApi = new AchievementsApi();
export default achievementsApi;
